import { } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronLeft,
  Bot,
  Settings,
  MessageCircle,
  Shield,
  Zap,
  Brain,
  TrendingUp,
  Target,
  Clock,
  Sparkles,
  Award,
} from 'lucide-react';
import type { Bridgelet } from '../utils/api';

interface BridgeletPageProps {
  bridgelet: Bridgelet;
  onBack: () => void;
  onEdit: () => void;
}

export function BridgeletPage({ bridgelet, onBack, onEdit }: BridgeletPageProps) {
  const getToneIcon = (tone: string) => {
    switch (tone) {
      case 'friendly':
        return '😊';
      case 'cautious':
        return '🧐';
      case 'professional':
        return '💼';
      default:
        return '🤖';
    }
  };

  const getToneLabel = (tone: string) => {
    switch (tone) {
      case 'friendly':
        return 'เป็นมิตร';
      case 'cautious':
        return 'ระมัดระวัง';
      case 'professional':
        return 'เป็นทางการ';
      default:
        return tone;
    }
  };

  const getStyleLabel = (style: string) => {
    switch (style) {
      case 'aggressive':
        return 'เจรจาต่อรองแรง';
      case 'balanced':
        return 'สมดุล';
      case 'conservative':
        return 'ระมัดระวัง';
      default:
        return style;
    }
  };

  const getRiskLabel = (risk: string) => {
    switch (risk) {
      case 'low':
        return 'ต่ำ';
      case 'medium':
        return 'ปานกลาง';
      case 'high':
        return 'สูง';
      default:
        return risk;
    }
  };

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <button
          onClick={onBack}
          className="p-2 rounded-xl glass hover:bg-white/15 transition-all active:scale-95"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        <h1 className="text-2xl font-bold text-white">AI Partner</h1>
        <button
          onClick={onEdit}
          className="p-2 rounded-xl glass hover:bg-white/15 transition-all active:scale-95"
        >
          <Settings className="w-6 h-6 text-white" />
        </button>
      </motion.div>

      {/* Bridgelet Profile */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass rounded-2xl p-6 text-center"
      >
        <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-5xl shadow-[0_0_40px_rgba(20,184,166,0.8)] animate-pulse-glow">
          {getToneIcon(bridgelet.persona.tone)}
        </div>
        <h2 className="text-3xl font-bold text-white mb-2">{bridgelet.name}</h2>
        <div className="flex items-center justify-center gap-2 mb-4">
          <span className="px-3 py-1 rounded-full bg-teal-500/20 text-teal-300 text-sm border border-teal-400/30">
            {bridgelet.status === 'active' ? 'ใช้งาน' : bridgelet.status === 'learning' ? 'กำลังเรียนรู้' : 'ไม่ใช้งาน'}
          </span>
        </div>
        <p className="text-white/60">
          ฉันจะเป็นผู้ช่วยคู่ใจของคุณ<br />
          ในการเจรจาและดูแลทุกดีลให้ปลอดภัย
        </p>
      </motion.div>

      {/* Performance Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <Award className="w-5 h-5 text-teal-300" />
          ผลงาน
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="glass rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-teal-300" />
              <p className="text-sm text-white/60">ดีลสำเร็จ</p>
            </div>
            <p className="text-3xl font-bold text-white">{bridgelet.stats.dealsCompleted}</p>
            <div className="mt-2 h-2 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min((bridgelet.stats.dealsCompleted / 50) * 100, 100)}%` }}
                transition={{ duration: 1, delay: 0.3 }}
                className="h-full bg-gradient-to-r from-teal-500 to-cyan-500"
              />
            </div>
          </div>

          <div className="glass rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-green-300" />
              <p className="text-sm text-white/60">อัตราสำเร็จ</p>
            </div>
            <p className="text-3xl font-bold text-green-400">{bridgelet.stats.successRate}%</p>
            <div className="mt-2 h-2 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${bridgelet.stats.successRate}%` }}
                transition={{ duration: 1, delay: 0.4 }}
                className="h-full bg-gradient-to-r from-green-500 to-emerald-500"
              />
            </div>
          </div>

          <div className="glass rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-cyan-300" />
              <p className="text-sm text-white/60">เวลาเฉลี่ย</p>
            </div>
            <p className="text-3xl font-bold text-white">{bridgelet.stats.avgNegotiationTime}</p>
            <p className="text-sm text-white/60 mt-1">นาที/ดีล</p>
          </div>

          <div className="glass rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-yellow-300" />
              <p className="text-sm text-white/60">ประหยัดได้</p>
            </div>
            <p className="text-2xl font-bold text-teal-300">
              ฿{bridgelet.stats.totalSaved.toLocaleString()}
            </p>
            <p className="text-sm text-white/60 mt-1">จากการเจรจา</p>
          </div>
        </div>
      </motion.div>

      {/* Personality Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <Brain className="w-5 h-5 text-teal-300" />
          บุคลิกภาพ
        </h3>
        <div className="glass rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-teal-300" />
              </div>
              <div>
                <p className="text-sm text-white/60">น้ำเสียง</p>
                <p className="text-white font-semibold">{getToneLabel(bridgelet.persona.tone)}</p>
              </div>
            </div>
            <span className="text-2xl">{getToneIcon(bridgelet.persona.tone)}</span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <Zap className="w-5 h-5 text-cyan-300" />
              </div>
              <div>
                <p className="text-sm text-white/60">สไตล์เจรจา</p>
                <p className="text-white font-semibold">
                  {getStyleLabel(bridgelet.persona.negotiationStyle)}
                </p>
              </div>
            </div>
            <span className="text-2xl">
              {bridgelet.persona.negotiationStyle === 'aggressive' ? '⚡' :
               bridgelet.persona.negotiationStyle === 'balanced' ? '⚖️' : '🛡️'}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-purple-300" />
              </div>
              <div>
                <p className="text-sm text-white/60">ความเสี่ยงที่ยอมรับ</p>
                <p className="text-white font-semibold">{getRiskLabel(bridgelet.persona.riskTolerance)}</p>
              </div>
            </div>
            <span className="text-2xl">
              {bridgelet.persona.riskTolerance === 'low' ? '🛡️' :
               bridgelet.persona.riskTolerance === 'medium' ? '⚖️' : '🚀'}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Bot className="w-5 h-5 text-emerald-300" />
              </div>
              <div>
                <p className="text-sm text-white/60">ภาษา</p>
                <p className="text-white font-semibold">
                  {bridgelet.persona.language === 'th' ? 'ไทย' : 'English'}
                </p>
              </div>
            </div>
            <span className="text-2xl">{bridgelet.persona.language === 'th' ? '🇹🇭' : '🇬🇧'}</span>
          </div>
        </div>
      </motion.div>

      {/* Action Button */}
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        onClick={onEdit}
        whileTap={{ scale: 0.95 }}
        className="w-full py-4 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold shadow-[0_0_30px_rgba(20,184,166,0.6)] hover:shadow-[0_0_40px_rgba(20,184,166,0.8)] transition-all flex items-center justify-center gap-2"
      >
        <Settings className="w-5 h-5" />
        ปรับแต่งบุคลิกภาพ
      </motion.button>
    </div>
  );
}
